/* ============================================================
   cc-cart-drawer.js — behavior port of Empire's cart drawer
   (Phase 4 item 1) onto Dawn's cart-drawer internals.

   Internals evolved per Tony's ruling (1:1 UX, new plumbing):
   - Dawn's CartDrawer element stays; we patch open/renderContents
     (lean method overrides, precedent: cc-gallery-variant-sync).
   - Server-rendered state travels in <cc-cart-drawer-hub>; its
     connectedCallback re-fires on every section swap = the render
     hook. NO MutationObserver.
   - Cart mutations go through a serialized write lane (Empire
     lesson: concurrent /cart/* writes drop lines) and request
     section HTML, then publish Dawn's cartUpdate (source
     'cart-items' = sections already painted).
   - Loyalty (LoyaltyLion) + Klaviyo are no-app-safe: they stay
     dormant until the SDKs exist. Phase 6 QA required.
   ============================================================ */

(() => {
  'use strict';
  if (window.ccCartDrawer) return;

  /* ---------- config ---------- */
  const configEl = document.getElementById('cc-cart-drawer-config');
  if (!configEl) return;
  let config = {};
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const drawer = () => document.querySelector('cart-drawer');
  const q = (sel) => {
    const d = drawer();
    return d ? d.querySelector(sel) : null;
  };

  /* i18n — hostname-based (es.chinchileproducts.com = Weglot proxy).
     Server-rendered text is translated by Weglot; tr() covers JS-injected strings. */
  const isES = location.hostname.indexOf('es.') === 0;
  const tr = (en, es) => (isES ? es : en);

  /* ---------- money ---------- */
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, config.moneyFormat);
    }
    const fmt = config.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const noDec = Math.round(cents / 100).toString();
    const comma = amount.replace('.', ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, comma);
  }
  function formatMoneyShort(cents) {
    const dollars = cents / 100;
    return dollars % 1 === 0 ? '$' + dollars : '$' + dollars.toFixed(2);
  }

  /* ---------- tracking (Klaviyo + dataLayer; no-app safe) ---------- */
  function trackEvent(name, data) {
    data = data || {};
    data.timestamp = Date.now();
    if (window.dataLayer) {
      const obj = { event: name };
      for (const k in data) obj[k] = data[k];
      window.dataLayer.push(obj);
    }
    if (window._learnq) {
      try {
        window._learnq.push(['track', name, data]);
      } catch (e) {
        /* noop */
      }
    }
  }

  /* ---------- serialized cart-write lane ---------- */
  let cartWriteLane = Promise.resolve();
  function queueCartWrite(task) {
    const run = cartWriteLane.then(task, task);
    cartWriteLane = run.catch(() => {});
    return run;
  }

  function jsonHeaders() {
    if (typeof window.fetchConfig === 'function') return fetchConfig('javascript');
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
    };
  }

  const SECTIONS = 'cart-drawer,cart-icon-bubble';

  /* ---------- state (persists across section swaps) ---------- */
  let state = { items: [], note: '', totalPrice: 0, itemCount: 0 };
  let hubData = { realCount: 0, subtotalNr: 0, insKey: '' };
  let pendingQty = {}; /* key -> target qty */
  let maxQty = {}; /* key -> learned stock ceiling */
  let qtySyncTimer = null;
  let qtySyncInFlight = false;
  let holdState = null;
  let lastChangeTime = 0;
  let isManagingRewards = false;
  let activePickerTier = null;
  let lastUpsellSig = null;
  let lastUpsellHTML = '';
  let notesOpen = false;
  let noteSaveTimer = null;
  let openedAt = 0;
  let loyaltyExpanded = false;
  let lastUnlockedTiers = -1;
  let isFlashing = false;

  const tiers = (config.tiers || []).filter((t) => t && t.amount > 0);
  tiers.sort((a, b) => a.amount - b.amount);
  const maxTierAmount = tiers.length ? tiers[tiers.length - 1].amount : 0;

  function findItem(key) {
    return state.items.find((it) => it.key === key) || null;
  }
  function effectiveQty(item) {
    return Object.prototype.hasOwnProperty.call(pendingQty, item.key) ? pendingQty[item.key] : item.qty;
  }
  /* BXGY split-line support (2026-07-08, ported from the live-Empire fix):
     a quantity shortfall after /cart/change is only a TRUE stock cap when the
     variant's TOTAL across all split lines fell short — automatic Buy X Get Y
     discounts split one variant into several lines and re-hash their keys on
     every change, which otherwise reads as "out of stock" when it isn't. */
  /* plan-aware (2026-10-02): a subscription line of the same variant is a
     separate line with its own key — never pool it with the one-time lines */
  function planIdOf(it) {
    return it && it.plan && it.plan.id ? it.plan.id : null;
  }
  function variantTotalInState(variantId, planId) {
    let t = 0;
    for (const it of state.items) {
      if (it.variantId === variantId && planIdOf(it) === (planId || null)) t += it.qty;
    }
    return t;
  }
  function variantTotalInCart(cartItems, variantId, planId) {
    let t = 0;
    for (const it of cartItems || []) {
      const p = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan ? it.selling_plan_allocation.selling_plan.id : null;
      if (it.variant_id === variantId && p === (planId || null)) t += it.quantity;
    }
    return t;
  }
  function capPre(key) {
    const it = findItem(key);
    return it
      ? { variantId: it.variantId, planId: planIdOf(it), lineQty: it.qty, variantTotal: variantTotalInState(it.variantId, planIdOf(it)) }
      : null;
  }
  function capCheck(pre, cartItems, target) {
    if (!pre || !pre.variantId) return true; /* no snapshot — keep the old behavior */
    const expected = pre.variantTotal + (target - pre.lineQty);
    return variantTotalInCart(cartItems, pre.variantId, pre.planId) < expected;
  }
  function optimisticSubtotal() {
    /* server total (already includes discounts) + pending qty deltas */
    let total = state.totalPrice || 0;
    for (const it of state.items) {
      if (Object.prototype.hasOwnProperty.call(pendingQty, it.key)) {
        total += (pendingQty[it.key] - it.qty) * it.price;
      }
    }
    return total;
  }
  function nonRewardSubtotal() {
    let total = 0;
    for (const it of state.items) {
      if (it.reward || it.ins || it.qty <= 0) continue;
      total += Object.prototype.hasOwnProperty.call(pendingQty, it.key)
        ? pendingQty[it.key] * it.price
        : it.line;
    }
    return total;
  }

  /* ---------- animated subtotal (quick count-up roll) ---------- */
  let lastSubtotalCents = null;
  let displaySubtotalCents = null;
  let subtotalRAF = null;
  let subtotalLastTs = null;
  let subtotalRemaining = 0;
  const SUBTOTAL_ROLL_MS = 150;
  const prefersReducedMotion = !!(
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  function subtotalEl() {
    return q('[data-cc-drawer-subtotal]');
  }

  function animateSubtotal(toCents) {
    const el = subtotalEl();
    if (!el) return;
    toCents = Math.round(toCents);
    lastSubtotalCents = toCents;

    if (displaySubtotalCents === null || prefersReducedMotion) {
      if (subtotalRAF) {
        cancelAnimationFrame(subtotalRAF);
        subtotalRAF = null;
      }
      displaySubtotalCents = toCents;
      el.textContent = formatMoney(toCents);
      return;
    }
    if (subtotalRAF === null && Math.round(displaySubtotalCents) === toCents) {
      displaySubtotalCents = toCents;
      el.textContent = formatMoney(toCents);
      return;
    }
    subtotalRemaining = SUBTOTAL_ROLL_MS;
    if (subtotalRAF === null) {
      subtotalLastTs = null;
      subtotalRAF = requestAnimationFrame(stepSubtotal);
    }
  }

  function stepSubtotal(now) {
    const el = subtotalEl();
    if (!el || displaySubtotalCents === null || lastSubtotalCents === null) {
      subtotalRAF = null;
      return;
    }
    if (subtotalLastTs === null) subtotalLastTs = now;
    const dt = now - subtotalLastTs;
    subtotalLastTs = now;

    const diff = lastSubtotalCents - displaySubtotalCents;
    if (subtotalRemaining <= dt || Math.abs(diff) <= 0.5) {
      displaySubtotalCents = lastSubtotalCents;
      el.textContent = formatMoney(lastSubtotalCents);
      subtotalRAF = null;
      subtotalLastTs = null;
      return;
    }
    displaySubtotalCents += diff * (dt / subtotalRemaining);
    subtotalRemaining -= dt;
    el.textContent = formatMoney(Math.round(displaySubtotalCents));
    subtotalRAF = requestAnimationFrame(stepSubtotal);
  }

  /* ---------- section painting ---------- */
  function swapIconBubble(html) {
    /* Dawn contract: the fetched section's inner HTML fills the <a id=cart-icon-bubble>
       itself (renderContents does the same) — never touch the enclosing section */
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('.shopify-section');
    if (fresh) bubble.innerHTML = fresh.innerHTML;
  }

  function toggleEmptyClasses(itemCount) {
    const d = drawer();
    if (!d) return;
    d.classList.toggle('is-empty', itemCount === 0);
    const cdi = d.querySelector('cart-drawer-items');
    if (cdi) cdi.classList.toggle('is-empty', itemCount === 0);
    const checkout = q('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = itemCount === 0;
  }

  /* Auto-close once the cart is truly empty. Lives in ONE place because the
     "last item" usually leaves in two steps: the user deletes the final real
     item (count still 1 — the auto-added protection/reward line survives it),
     then the orphan-cleanup removes that line. The direct delete paths'
     item_count check never saw 0, and the cleanup paths had no close at all —
     drawer stayed open on an empty cart (Tony 2026-08-06). */
  function closeSoonIfEmpty(itemCount) {
    if (itemCount !== 0) return;
    /* 500ms: long enough to register the empty state, short enough not to
       feel slow after the cleanup round trips (was Empire's 800ms) */
    setTimeout(() => {
      const d = drawer();
      if (d && typeof d.close === 'function') d.close();
    }, 500);
  }

  function paintFull(sections, itemCount) {
    const d = drawer();
    if (!d || !sections || !sections['cart-drawer']) return;
    const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const freshInner = doc.querySelector('.drawer__inner');
    const inner = d.querySelector('.drawer__inner');
    if (freshInner && inner) {
      /* Anti-flicker (Tony 2026-08-06): the upsell grid is CLIENT-rendered,
         so every swap landed the server's EMPTY wrap and loadUpsells
         re-created the images a beat later — visible flash on every write.
         Carry the LIVE node across the swap. Also keep the panel's scroll
         (inner IS the scroller) — full swaps snapped the list to the top. */
      const liveUpsell = q('[data-cc-drawer-upsell]');
      const keepUpsell =
        liveUpsell && liveUpsell.querySelector('[data-cc-drawer-upsell-grid] > *') ? liveUpsell : null;
      const scrollTop = inner.scrollTop;
      inner.innerHTML = freshInner.innerHTML; /* hub re-fires */
      if (keepUpsell) {
        const freshUpsell = d.querySelector('[data-cc-drawer-upsell]');
        if (freshUpsell) freshUpsell.replaceWith(keepUpsell);
      }
      inner.scrollTop = scrollTop;
    }
    swapIconBubble(sections['cart-icon-bubble']);
    if (typeof itemCount === 'number') toggleEmptyClasses(itemCount);
  }

  function paintSurgical(sections, itemCount) {
    const d = drawer();
    if (!d || !sections || !sections['cart-drawer']) return;
    const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    /* keep the items list DOM untouched (no blink) — refresh the aggregate blocks */
    ['.cc-drawer__rewards', '.drawer__footer', 'cc-cart-drawer-hub'].forEach((sel) => {
      const fresh = doc.querySelector(sel);
      const cur = d.querySelector(sel);
      if (!fresh || !cur) return;
      if (sel === '.drawer__footer') {
        /* the footer carries the client-rendered upsell grid — swapping it
           wholesale flashed the row on EVERY qty tap (Tony 2026-08-06).
           Carry the live node across, same as paintFull. */
        const liveUpsell = cur.querySelector('[data-cc-drawer-upsell]');
        const keepUpsell =
          liveUpsell && liveUpsell.querySelector('[data-cc-drawer-upsell-grid] > *') ? liveUpsell : null;
        cur.replaceWith(fresh);
        if (keepUpsell) {
          const freshUpsell = fresh.querySelector('[data-cc-drawer-upsell]');
          if (freshUpsell) freshUpsell.replaceWith(keepUpsell);
        }
        return;
      }
      cur.replaceWith(fresh); /* hub replaceWith re-fires connectedCallback */
    });
    swapIconBubble(sections['cart-icon-bubble']);
    if (typeof itemCount === 'number') toggleEmptyClasses(itemCount);
  }

  function publishCartUpdate(cartData) {
    /* PUB_SUB_EVENTS is a top-level `const` (Dawn constants.js): a global
       binding but NOT a window property — `window.PUB_SUB_EVENTS` was always
       undefined, so this never published (2026-10-01: the PDP "(N in cart)"
       label stayed stale after drawer edits). Test the bare identifier. */
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      /* source 'cart-items' = "sections already painted" (Dawn's own convention) */
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cartData });
    }
  }

  function refreshFromServer(mode) {
    return Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) =>
        r.json()
      ),
      fetch(`${config.cartUrl}?sections=${SECTIONS}`, { credentials: 'same-origin' }).then((r) => r.json()),
    ]).then(([cart, sections]) => {
      /* surgical only holds while rows exist — an emptied cart needs the
         full empty-state render */
      if (mode === 'surgical' && cart.item_count !== 0) paintSurgical(sections, cart.item_count);
      else paintFull(sections, cart.item_count);
      return cart;
    });
  }

  /* ---------- optimistic quantity engine ---------- */
  function itemsList() {
    return q('[data-cc-drawer-items]');
  }
  function lineEl(key) {
    const list = itemsList();
    return list ? list.querySelector(`[data-cc-item-key="${CSS.escape(key)}"]`) : null;
  }

  function applyOptimistic(key) {
    const item = findItem(key);
    if (!item) return;
    const qty = Object.prototype.hasOwnProperty.call(pendingQty, key) ? pendingQty[key] : item.qty;
    const el = lineEl(key);
    if (el) {
      if (qty <= 0) {
        el.remove();
      } else {
        /* merged BXGY rows display line qty + absorbed free qty; their price is
           a combined strike/final pair — leave it to the server repaint */
        const qtyExtra = parseInt(el.getAttribute('data-cc-qty-extra'), 10) || 0;
        const disp = el.querySelector('[data-cc-qty-display]');
        if (disp) disp.textContent = qty + qtyExtra;
        const priceEl = el.querySelector('.cc-drawer__item-price');
        if (priceEl && !qtyExtra) priceEl.textContent = formatMoney(qty * item.price);
        const minusBtn = el.querySelector('[data-cc-qty-change="-1"]');
        if (minusBtn) minusBtn.disabled = qty + qtyExtra <= 1;
        const atMax = Object.prototype.hasOwnProperty.call(maxQty, key) && qty >= maxQty[key];
        const plusBtn = el.querySelector('[data-cc-qty-change="1"]');
        if (plusBtn) plusBtn.disabled = atMax;
        if (!atMax) {
          const note = el.querySelector('.cc-drawer__max-note');
          if (note) note.remove();
        }
      }
    }
    /* subtotal rolls on release, not per tap */
    /* cc-volume-nudge repaints per-line nudges from effectiveItems() */
    window.dispatchEvent(new CustomEvent('cc:cart-optimistic', { detail: { key: key } }));
  }

  function showMaxMessage(key) {
    const el = lineEl(key);
    if (!el || el.querySelector('.cc-drawer__max-note')) return;
    const plusBtn = el.querySelector('[data-cc-qty-change="1"]');
    if (plusBtn) plusBtn.disabled = true;
    const note = document.createElement('span');
    note.className = 'cc-drawer__max-note';
    note.textContent = tr("That's all we have in stock", 'Eso es todo lo que tenemos');
    const qtyRow = el.querySelector('.cc-drawer__item-priceqty') || el.querySelector('.cc-drawer__item-qty');
    if (qtyRow) qtyRow.parentNode.insertBefore(note, qtyRow.nextSibling);
  }

  function setQtyTarget(key, qty) {
    const item = findItem(key);
    if (!item) return;
    qty = Math.max(0, qty);
    if (Object.prototype.hasOwnProperty.call(maxQty, key) && qty > maxQty[key]) qty = maxQty[key];
    if (qty === effectiveQty(item)) return;
    pendingQty[key] = qty;
    lastChangeTime = Date.now();
    applyOptimistic(key);
    if (!holdState) scheduleQtySync();
  }

  function bumpQty(key, delta) {
    const item = findItem(key);
    if (!item) return;
    /* stepper floors at 1 (removal only via X) and caps at a learned ceiling */
    let target = Math.max(1, effectiveQty(item) + delta);
    if (Object.prototype.hasOwnProperty.call(maxQty, key)) target = Math.min(target, maxQty[key]);
    if (target === effectiveQty(item)) {
      if (holdState) stopHold();
      return;
    }
    setQtyTarget(key, target);
  }

  function scheduleQtySync() {
    if (qtySyncTimer) clearTimeout(qtySyncTimer);
    qtySyncTimer = setTimeout(runQtySync, 350);
  }

  /* Remove EVERY line of a variant (merged BXGY rows). Deleting by a single
     key silently failed when the discount re-hashed keys mid-flight and the
     leftover free line re-priced back to paid — the item "came back". The
     cart is re-fetched before each change so keys are always fresh. */
  function removeVariantLines(variantId) {
    let removedCents = 0;
    for (const it of state.items) {
      if (it.variantId !== variantId) continue;
      delete pendingQty[it.key];
      delete maxQty[it.key];
      removedCents += it.line;
      const el = lineEl(it.key);
      if (el) el.remove();
    }
    window.dispatchEvent(new CustomEvent('cc:cart-optimistic', { detail: { variantId: variantId } }));
    animateSubtotal(Math.max(0, optimisticSubtotal() - removedCents));
    lastChangeTime = Date.now();

    queueCartWrite(() => {
      let attempts = 0;
      const step = () =>
        fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => {
            attempts += 1;
            const line = (cart.items || []).find(
              (it) => it.variant_id === variantId && !(it.properties && Object.keys(it.properties).length)
            );
            if (!line || attempts > 4) return cart;
            return changeRequest(line.key, 0, false)
              .then((r) => (r.ok ? r.json() : null))
              .then(() => step());
          });
      return step();
    })
      .then(() =>
        /* surgical: the removed rows are already gone optimistically — a full
           repaint flashed every SURVIVING row (Tony 2026-08-06) */
        refreshFromServer('surgical').then((cart) => {
          trackEvent('cc_drawer_item_removed', { variant_id: variantId });
          publishCartUpdate(cart);
          closeSoonIfEmpty(cart.item_count);
        })
      )
      .catch((err) => {
        console.error('[cc-cart-drawer] variant remove error:', err);
        refreshFromServer('full').catch(() => {});
      });
  }

  function changeRequest(key, qty, withSections) {
    const body = { id: key, quantity: qty };
    /* keep the selling plan on quantity changes (Shopify Subscriptions, 2026-10-02) */
    const planItem = findItem(key);
    if (planItem && planItem.plan && planItem.plan.id) body.selling_plan = planItem.plan.id;
    if (withSections) {
      body.sections = SECTIONS;
      body.sections_url = window.location.pathname;
    }
    return fetch('/cart/change.js', {
      ...jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  }

  function runQtySync() {
    qtySyncTimer = null;
    if (qtySyncInFlight) {
      scheduleQtySync();
      return;
    }
    const keys = Object.keys(pendingQty);
    if (!keys.length) return;
    qtySyncInFlight = true;

    const targets = {};
    const pres = {};
    keys.forEach((k) => {
      targets[k] = pendingQty[k];
      pres[k] = capPre(k); /* pre-change variant totals, for split-aware cap detection */
    });

    queueCartWrite(() => {
      let chain = Promise.resolve(null);
      keys.forEach((k, idx) => {
        const isLast = idx === keys.length - 1;
        chain = chain.then(() =>
          changeRequest(k, targets[k], isLast).then((r) => {
            if (r.ok) return r.json();
            /* rejected (e.g. stock cap) — recover with true state + fresh sections */
            return Promise.all([
              fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
                (r2) => r2.json()
              ),
              fetch(`${config.cartUrl}?sections=${SECTIONS}`, { credentials: 'same-origin' }).then((r2) =>
                r2.json()
              ),
            ]).then(([cart, sections]) => {
              cart.sections = sections;
              return cart;
            });
          })
        );
      });
      return chain;
    })
      .then((finalCart) => {
        qtySyncInFlight = false;
        lastChangeTime = Date.now();

        keys.forEach((k) => {
          if (pendingQty[k] === targets[k]) delete pendingQty[k];
        });

        if (finalCart && finalCart.items) {
          let needFullRebuild = false;
          keys.forEach((k) => {
            const el = lineEl(k);
            const srv = finalCart.items.find((it) => it.key === k) || null;

            if (targets[k] === 0) {
              if (el) el.remove();
              trackEvent('cc_drawer_item_removed', { item_key: k });
              return;
            }
            if (!srv || srv.quantity <= 0 || !el) {
              /* line key re-hashed (free-gift/automatic discount) or row missing —
                 rebuild from the server render so the item survives with its new key */
              needFullRebuild = true;
              return;
            }
            const rowExtra = parseInt(el.getAttribute('data-cc-qty-extra'), 10) || 0;
            const disp = el.querySelector('[data-cc-qty-display]');
            if (disp) disp.textContent = srv.quantity + rowExtra;
            const priceEl = el.querySelector('.cc-drawer__item-price');
            if (priceEl && !rowExtra) priceEl.textContent = formatMoney(srv.final_line_price);
            const minusBtn = el.querySelector('[data-cc-qty-change="-1"]');
            if (minusBtn) minusBtn.disabled = srv.quantity + rowExtra <= 1;
            if (srv.quantity !== targets[k] && !capCheck(pres[k], finalCart.items, targets[k])) {
              /* the automatic discount re-split this variant's lines — quantity
                 moved but stock is fine; repaint from server truth */
              needFullRebuild = true;
            } else if (srv.quantity < targets[k]) {
              maxQty[k] = srv.quantity;
              showMaxMessage(k);
            } else {
              const atMax = Object.prototype.hasOwnProperty.call(maxQty, k) && srv.quantity >= maxQty[k];
              const plusBtn = el.querySelector('[data-cc-qty-change="1"]');
              if (plusBtn) plusBtn.disabled = atMax;
              if (!atMax) {
                const note = el.querySelector('.cc-drawer__max-note');
                if (note) note.remove();
              }
            }
          });

          if (finalCart.sections) {
            if (needFullRebuild || finalCart.item_count === 0) paintFull(finalCart.sections, finalCart.item_count);
            else paintSurgical(finalCart.sections, finalCart.item_count);
          }
          publishCartUpdate(finalCart);
          closeSoonIfEmpty(finalCart.item_count);
        }

        if (Object.keys(pendingQty).length) {
          Object.keys(pendingQty).forEach(applyOptimistic);
          scheduleQtySync();
        }
      })
      .catch((err) => {
        qtySyncInFlight = false;
        console.error('[cc-cart-drawer] qty sync error:', err);
        refreshFromServer('full').catch(() => {});
      });
  }

  /* ---------- press-and-hold ramp ---------- */
  function probeHoldCap() {
    if (!holdState || qtySyncInFlight) return;
    const key = holdState.key;
    if (!Object.prototype.hasOwnProperty.call(pendingQty, key)) return;
    const target = pendingQty[key];
    if (target <= 0) return;
    const pre = capPre(key); /* pre-change variant totals, for split-aware cap detection */
    qtySyncInFlight = true;
    queueCartWrite(() => changeRequest(key, target, false).then((r) => (r.ok ? r.json() : null)))
      .then((cart) => {
        qtySyncInFlight = false;
        if (!cart || !cart.items) return;
        const srv = cart.items.find((it) => it.key === key) || null;
        const local = findItem(key);
        if (local && srv) local.qty = srv.quantity;
        if (local) state.totalPrice = cart.total_price;
        if (srv && srv.quantity < target && capCheck(pre, cart.items, target)) {
          maxQty[key] = srv.quantity;
          pendingQty[key] = srv.quantity;
          const el = lineEl(key);
          if (el) {
            const disp = el.querySelector('[data-cc-qty-display]');
            if (disp) disp.textContent = srv.quantity;
            const pe = el.querySelector('.cc-drawer__item-price');
            if (pe) pe.textContent = formatMoney(srv.final_line_price);
          }
          showMaxMessage(key);
          stopHold();
        }
      })
      .catch(() => {
        qtySyncInFlight = false;
      });
  }

  function stopHold() {
    if (!holdState) return;
    if (holdState.timer) clearTimeout(holdState.timer);
    if (holdState.probe) clearInterval(holdState.probe);
    holdState = null;
    animateSubtotal(optimisticSubtotal()); /* roll once, on release */
    scheduleQtySync();
  }

  function startHold(key, delta) {
    stopHold();
    holdState = { key: key, delta: delta, delay: 350 };
    if (delta > 0) holdState.probe = setInterval(probeHoldCap, 350);
    bumpQty(key, delta);
    if (!holdState) return;
    holdState.timer = setTimeout(function tick() {
      if (!holdState) return;
      bumpQty(holdState.key, holdState.delta);
      if (!holdState) return;
      holdState.delay = Math.max(140, holdState.delay - 25);
      holdState.timer = setTimeout(tick, holdState.delay);
    }, 500);
  }

  /* ---------- rewards: auto-add / remove + gift picker ---------- */
  function rewardsInCart() {
    const map = {};
    for (const it of state.items) {
      if (it.reward) map[it.reward] = it.key;
    }
    return map;
  }

  function manageRewardItems() {
    if (isManagingRewards) return;
    const inCartMap = rewardsInCart();

    /* orphaned rewards (tier disabled) */
    const activeTierIds = {};
    tiers.forEach((t) => {
      activeTierIds[t.id] = true;
    });
    for (const rewardId in inCartMap) {
      if (!activeTierIds[rewardId]) {
        removeRewardItem(inCartMap[rewardId]);
        return;
      }
    }
    if (!config.rewardsEnabled || tiers.length === 0) return;

    const subtotal = nonRewardSubtotal();

    let highestUnlocked = null;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (tiers[i].type !== 'visual' && subtotal >= tiers[i].amount) {
        highestUnlocked = tiers[i];
        break;
      }
    }

    for (const tier of tiers) {
      if (tier.type === 'visual') continue;
      const inCart = !!inCartMap[tier.id];
      if (inCart && (!highestUnlocked || tier.id !== highestUnlocked.id)) {
        removeRewardItem(inCartMap[tier.id]);
        return;
      }
    }

    if (!highestUnlocked) {
      hideGiftPicker();
      return;
    }
    const inCart = !!inCartMap[highestUnlocked.id];
    if (highestUnlocked.type === 'auto_add') {
      hideGiftPicker();
      if (!inCart && highestUnlocked.handle) addRewardProduct(highestUnlocked);
    } else if (highestUnlocked.type === 'choice') {
      if (!inCart) showGiftPicker(highestUnlocked);
      else hideGiftPicker();
    }
  }

  function addRewardProduct(tier) {
    if (isManagingRewards) return;
    isManagingRewards = true;
    lastChangeTime = Date.now();

    const props = { _cc_reward: tier.id };
    if (tier.note) props.Note = tier.note;

    const addVariant = (vid) => {
      queueCartWrite(() =>
        fetch('/cart/add.js', {
          ...jsonHeaders(),
          credentials: 'same-origin',
          body: JSON.stringify({ id: vid, quantity: 1, properties: props, sections: SECTIONS }),
        }).then((r) => r.json())
      )
        .then((resp) => {
          isManagingRewards = false;
          if (resp && resp.sections) paintFull(resp.sections);
          return refreshStateOnly().then(publishCartUpdate);
        })
        .catch((err) => {
          isManagingRewards = false;
          console.error('[cc-cart-drawer] reward add error:', err);
        });
    };

    if (/^\d+$/.test(tier.handle)) {
      addVariant(parseInt(tier.handle, 10));
      return;
    }
    fetch('/products/' + tier.handle + '.js', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((product) => {
        const v = (product.variants || []).find((x) => x.available);
        if (!v) {
          isManagingRewards = false;
          return;
        }
        addVariant(v.id);
      })
      .catch(() => {
        isManagingRewards = false;
      });
  }

  function removeRewardItem(key) {
    if (isManagingRewards) return;
    isManagingRewards = true;
    lastChangeTime = Date.now();
    queueCartWrite(() => changeRequest(key, 0, true).then((r) => r.json()))
      .then((cart) => {
        isManagingRewards = false;
        if (cart && cart.sections) paintFull(cart.sections, cart.item_count);
        publishCartUpdate(cart);
        if (cart) closeSoonIfEmpty(cart.item_count); /* orphan-gift cleanup emptied the cart */
      })
      .catch((err) => {
        isManagingRewards = false;
        console.error('[cc-cart-drawer] reward remove error:', err);
      });
  }

  /* fetch fresh /cart.js into state without repainting (post-add reconciliation) */
  function refreshStateOnly() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json())
      .catch(() => null);
  }

  /* ---------- gift picker ---------- */
  function pickerWrap() {
    return q('[data-cc-drawer-picker]');
  }
  function upsellWrap() {
    return q('[data-cc-drawer-upsell]');
  }

  function showGiftPicker(tier) {
    const wrap = pickerWrap();
    const list = q('[data-cc-drawer-picker-list]');
    if (!wrap || !list) return;
    if (activePickerTier && activePickerTier.id === tier.id && !wrap.hidden) return;
    activePickerTier = tier;

    const handles = (tier.choices || []).filter((h) => h && h.length > 0);
    if (!handles.length) {
      hideGiftPicker();
      return;
    }

    list.innerHTML =
      '<p style="text-align:center;color:#888;font-size:13px;">' + tr('Loading options...', 'Cargando opciones...') + '</p>';
    wrap.hidden = false;
    const uw = upsellWrap();
    if (uw) uw.hidden = true;

    /* choice formats: "handle", "handle:variantId", "variantId" */
    const parsed = handles.map((h) => {
      if (h.indexOf(':') !== -1) {
        const parts = h.split(':');
        return { handle: parts[0], forceVariantId: parseInt(parts[1], 10) };
      }
      if (/^\d+$/.test(h)) return { handle: null, forceVariantId: parseInt(h, 10) };
      return { handle: h, forceVariantId: null };
    });

    Promise.all(
      parsed.map((c) => {
        if (!c.handle) {
          return Promise.resolve({
            title: 'Gift Option',
            featured_image: null,
            variants: [{ id: c.forceVariantId, available: true }],
            _forceVid: c.forceVariantId,
          });
        }
        return fetch('/products/' + c.handle + '.js', { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .then((p) => {
            if (!p) return null;
            if (c.forceVariantId) p._forceVid = c.forceVariantId;
            return p;
          })
          .catch(() => null);
      })
    ).then((products) => {
      const valid = products.filter((p) => p && p.variants && p.variants.some((v) => v.available));
      if (!valid.length) {
        hideGiftPicker();
        return;
      }
      let html = '';
      for (const p of valid) {
        let vid = p._forceVid || '';
        if (!vid) {
          const v = p.variants.find((x) => x.available);
          vid = v ? v.id : '';
        }
        let variantTitle = '';
        if (p._forceVid && p.variants) {
          const fv = p.variants.find((x) => x.id === p._forceVid);
          if (fv && fv.title !== 'Default Title') variantTitle = fv.title;
        }
        const imgSrc = p.featured_image
          ? String(p.featured_image).replace(/(\.[a-z]+)(\?|$)/, '_200x$1$2')
          : '';
        html += '<div class="cc-drawer__picker-item" data-cc-picker-variant="' + vid + '" data-cc-picker-tier="' + tier.id + '">';
        if (imgSrc) html += '<img src="' + imgSrc + '" alt="' + escapeHtml(p.title) + '" width="56" height="56" loading="lazy">';
        html += '<span class="cc-drawer__picker-name">' + escapeHtml(p.title) + (variantTitle ? ' — ' + escapeHtml(variantTitle) : '') + '</span>';
        html += '<button type="button" class="cc-drawer__picker-btn" data-cc-picker-select>' + tr('Select', 'Seleccionar') + '</button>';
        html += '</div>';
      }
      list.innerHTML = html;
    });
  }

  function hideGiftPicker() {
    const wrap = pickerWrap();
    if (wrap) wrap.hidden = true;
    const uw = upsellWrap();
    if (uw && lastUpsellHTML) uw.hidden = false;
    activePickerTier = null;
  }

  function onPickerSelect(btn) {
    const row = btn.closest('[data-cc-picker-variant]');
    if (!row) return;
    const vid = row.getAttribute('data-cc-picker-variant');
    const tierId = row.getAttribute('data-cc-picker-tier');
    if (!vid || !tierId) return;

    btn.textContent = tr('Adding...', 'Agregando...');
    btn.disabled = true;

    const tier = tiers.find((t) => t.id === tierId) || null;
    const props = { _cc_reward: tierId };
    if (tier && tier.note) props.Note = tier.note;

    isManagingRewards = true;
    lastChangeTime = Date.now();

    queueCartWrite(() =>
      fetch('/cart/add.js', {
        ...jsonHeaders(),
        credentials: 'same-origin',
        body: JSON.stringify({ id: parseInt(vid, 10), quantity: 1, properties: props, sections: SECTIONS }),
      }).then((r) => r.json())
    )
      .then((resp) => {
        isManagingRewards = false;
        activePickerTier = null;
        if (resp && resp.sections) paintFull(resp.sections);
        return refreshStateOnly().then(publishCartUpdate);
      })
      .catch((err) => {
        isManagingRewards = false;
        console.error('[cc-cart-drawer] picker add error:', err);
        btn.textContent = tr('Select', 'Seleccionar');
        btn.disabled = false;
      });
  }

  /* ---------- cross-sell row (recommendations API) ---------- */
  function loadUpsells() {
    const wrap = upsellWrap();
    const grid = q('[data-cc-drawer-upsell-grid]');
    if (!wrap || !grid || !config.recommendationsUrl) return;
    if (activePickerTier) {
      wrap.hidden = true;
      return;
    }
    const realItems = state.items.filter((it) => !it.reward && !it.ins && it.qty > 0);
    if (!realItems.length) {
      wrap.hidden = true;
      lastUpsellSig = null;
      lastUpsellHTML = '';
      return;
    }
    /* anchor to the most recently added product (cart.items is newest-first) */
    const productId = realItems[0].productId;
    const sig = productId + '|' + realItems.map((it) => it.productId).sort().join(',');
    if (sig === lastUpsellSig) {
      if (lastUpsellHTML) {
        /* only touch the DOM if the grid actually lost its content — an
           unconditional innerHTML re-set recreated the <img> nodes on
           every render pass and flickered the row (Tony 2026-08-06) */
        if (grid.innerHTML !== lastUpsellHTML) grid.innerHTML = lastUpsellHTML;
        wrap.hidden = false;
      }
      return;
    }
    lastUpsellSig = sig;

    fetch(config.recommendationsUrl + '.json?product_id=' + productId + '&limit=6&intent=related', {
      credentials: 'same-origin',
    })
      .then((r) => r.json())
      .then((data) => {
        /* re-query: the footer (and the wrap/grid inside it) may have been
           swapped by Dawn's post-ATC double render while we were fetching */
        const wrapNow = upsellWrap();
        const gridNow = q('[data-cc-drawer-upsell-grid]');
        if (!wrapNow || !gridNow) return;
        if (!data.products || !data.products.length) {
          wrapNow.hidden = true;
          lastUpsellHTML = '';
          return;
        }
        const cartProductIds = state.items.map((it) => it.productId);
        const recs = data.products
          .filter((p) => {
            if (cartProductIds.indexOf(p.id) !== -1) return false;
            if (p.tags && p.tags.indexOf('no-upsell') !== -1) return false;
            if (!p.price || p.price <= 0) return false; /* never upsell a free product */
            if (!p.variants || !p.variants.length) return false;
            return p.variants.some((v) => v.available);
          })
          .slice(0, 3);
        if (!recs.length) {
          wrapNow.hidden = true;
          lastUpsellHTML = '';
          return;
        }
        let html = '';
        for (const p of recs) {
          const v = p.variants.find((x) => x.available);
          const variantId = v ? v.id : '';
          const imgSrc = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_200x$1$2') : '';
          html += '<div class="cc-drawer__upsell-item" data-cc-upsell-product="' + p.id + '" data-cc-upsell-variant="' + variantId + '" data-cc-upsell-price="' + p.price + '" data-cc-upsell-url="' + (p.url || '#') + '" data-cc-upsell-add>';
          if (imgSrc) html += '<img src="' + imgSrc + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="44" height="44">';
          html += '<div class="cc-drawer__upsell-info">';
          html += '<span class="cc-drawer__upsell-name">' + escapeHtml(p.title) + '</span>';
          html += '<span class="cc-drawer__upsell-price">' + formatMoney(p.price) + '</span>';
          html += '</div>';
          html += '<span class="cc-drawer__upsell-plus">+</span>';
          html += '</div>';
        }
        lastUpsellHTML = html;
        gridNow.innerHTML = html;
        wrapNow.hidden = !!activePickerTier;
      })
      .catch(() => {
        const wrapNow = upsellWrap();
        if (wrapNow) wrapNow.hidden = true;
      });
  }

  function onUpsellAdd(card) {
    const variantId = card.getAttribute('data-cc-upsell-variant');
    if (!variantId || card.classList.contains('is-adding')) return;
    card.classList.add('is-adding');
    const plus = card.querySelector('.cc-drawer__upsell-plus');
    if (plus) plus.textContent = '✓';
    lastChangeTime = Date.now();

    const priceCents = parseInt(card.getAttribute('data-cc-upsell-price'), 10) || 0;
    animateSubtotal(optimisticSubtotal() + priceCents);

    queueCartWrite(() =>
      fetch('/cart/add.js', {
        ...jsonHeaders(),
        credentials: 'same-origin',
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1, sections: SECTIONS }),
      }).then((r) => r.json())
    )
      .then((resp) => {
        trackEvent('cc_drawer_upsell_click', {
          product_id: card.getAttribute('data-cc-upsell-product'),
          product_name: (card.querySelector('.cc-drawer__upsell-name') || {}).textContent || '',
        });
        if (resp && resp.sections) paintFull(resp.sections);
        card.classList.remove('is-adding');
        if (plus) plus.textContent = '+';
        return refreshStateOnly().then(publishCartUpdate);
      })
      .catch((err) => {
        console.error('[cc-cart-drawer] upsell add error:', err);
        card.classList.remove('is-adding');
        if (plus) plus.textContent = '+';
        refreshFromServer('full').catch(() => {});
      });
  }

  /* ---------- gift message (order note) ---------- */
  function restoreNotesUI() {
    const box = q('[data-cc-notes-box]');
    const toggle = q('[data-cc-notes-toggle]');
    const input = q('[data-cc-notes-input]');
    if (!box || !toggle || !input) return;
    box.hidden = !notesOpen;
    toggle.textContent = notesOpen ? tr('Hide', 'Ocultar') : tr('Gift Message', 'Mensaje de regalo');
    if (!input.value && state.note) input.value = state.note;
  }

  function onNotesToggle() {
    notesOpen = !notesOpen;
    restoreNotesUI();
    if (notesOpen) {
      const input = q('[data-cc-notes-input]');
      const scroller = q('.drawer__inner');
      requestAnimationFrame(() => {
        if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
        if (input) input.focus({ preventScroll: true });
      });
    }
  }

  function onNotesInput(input) {
    clearTimeout(noteSaveTimer);
    noteSaveTimer = setTimeout(() => {
      const value = input.value;
      state.note = value;
      queueCartWrite(() =>
        fetch('/cart/update.js', {
          ...jsonHeaders(),
          credentials: 'same-origin',
          body: JSON.stringify({ note: value }),
        })
      );
    }, 500);
  }

  /* ---------- package protection ---------- */
  const insEnabled = config.insuranceEnabled === true;
  const insHandle = config.insuranceHandle || '';
  const insTitle = config.insuranceTitle || 'Package Protection';
  const insSub = config.insuranceSubtext || '';
  const insDefaultOn = config.insuranceDefaultOn === true;
  const INS_OPTOUT_KEY = 'cc_ins_optout';

  let insVariantId = null;
  let insPriceCents = 0;
  let insReady = false;
  let insInitStarted = false;
  let isManagingInsurance = false;
  let insAutoAddAttempted = false;

  function insOptedOut() {
    try {
      return sessionStorage.getItem(INS_OPTOUT_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
  function setInsOptOut(v) {
    try {
      sessionStorage.setItem(INS_OPTOUT_KEY, v ? '1' : '0');
    } catch (e) {
      /* noop */
    }
  }

  function initInsurance() {
    if (!insEnabled || !insHandle || insInitStarted) return;
    insInitStarted = true;
    fetch('/products/' + insHandle + '.js', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((product) => {
        if (!product || !product.variants || !product.variants.length) return;
        const v = product.variants.find((x) => x.available) || product.variants[0];
        insVariantId = v.id;
        insPriceCents = v.price;
        insReady = true;
        updateInsurance();
      })
      .catch(() => {});
  }

  function updateInsurance() {
    const wrap = q('[data-cc-protection]');
    if (!insEnabled || !wrap) return;
    const titleEl = q('[data-cc-protection-title]');
    const subEl = q('[data-cc-protection-sub]');
    const priceEl = q('[data-cc-protection-price]');
    if (titleEl) titleEl.textContent = insTitle;
    if (subEl) {
      subEl.textContent = insSub;
      subEl.style.display = insSub ? '' : 'none';
    }
    if (!insReady) {
      wrap.hidden = true;
      return;
    }
    if (priceEl) priceEl.textContent = formatMoney(insPriceCents);

    const inCart = !!hubData.insKey;
    if (hubData.realCount === 0) {
      wrap.hidden = true;
      if (inCart && !isManagingInsurance) removeInsurance();
      return;
    }
    wrap.hidden = false;
    const check = q('[data-cc-protection-check]');
    if (check) check.checked = inCart;

    if (
      insDefaultOn &&
      !inCart &&
      !insOptedOut() &&
      !insAutoAddAttempted &&
      !isManagingInsurance &&
      !isManagingRewards
    ) {
      insAutoAddAttempted = true;
      addInsurance();
    }
  }

  function onInsToggle(check) {
    if (!insReady || isManagingInsurance) {
      check.checked = !!hubData.insKey;
      return;
    }
    if (check.checked) {
      setInsOptOut(false);
      animateSubtotal(optimisticSubtotal() + insPriceCents);
      addInsurance();
    } else {
      setInsOptOut(true);
      animateSubtotal(Math.max(0, optimisticSubtotal() - insPriceCents));
      removeInsurance();
    }
  }

  function setInsDisabled(disabled) {
    const check = q('[data-cc-protection-check]');
    if (check) check.disabled = disabled;
  }

  function addInsurance() {
    if (isManagingInsurance || !insVariantId) return;
    isManagingInsurance = true;
    lastChangeTime = Date.now();
    setInsDisabled(true);
    queueCartWrite(() =>
      fetch('/cart/add.js', {
        ...jsonHeaders(),
        credentials: 'same-origin',
        body: JSON.stringify({
          id: insVariantId,
          quantity: 1,
          properties: { _cc_insurance: 'true' },
          sections: SECTIONS,
        }),
      }).then((r) => r.json())
    )
      .then((resp) => {
        isManagingInsurance = false;
        setInsDisabled(false);
        trackEvent('cc_protection_added', { price: insPriceCents });
        /* protection isn't a visible line — surgical repaint, no list blink */
        if (resp && resp.sections) paintSurgical(resp.sections);
        return refreshStateOnly().then(publishCartUpdate);
      })
      .catch((err) => {
        isManagingInsurance = false;
        setInsDisabled(false);
        console.error('[cc-cart-drawer] protection add error:', err);
      });
  }

  function removeInsurance() {
    if (isManagingInsurance) return;
    isManagingInsurance = true;
    lastChangeTime = Date.now();
    setInsDisabled(true);
    queueCartWrite(() => {
      const key = hubData.insKey || (state.items.find((it) => it.ins) || {}).key;
      if (!key) return Promise.resolve(null);
      return changeRequest(key, 0, true).then((r) => r.json());
    })
      .then((cart) => {
        isManagingInsurance = false;
        setInsDisabled(false);
        if (!cart) return;
        trackEvent('cc_protection_removed', {});
        if (cart.sections) paintSurgical(cart.sections, cart.item_count);
        publishCartUpdate(cart);
        closeSoonIfEmpty(cart.item_count); /* orphan-protection cleanup emptied the cart */
      })
      .catch((err) => {
        isManagingInsurance = false;
        setInsDisabled(false);
        console.error('[cc-cart-drawer] protection remove error:', err);
      });
  }

  /* ---------- loyalty (LoyaltyLion — Phase 6 QA) ---------- */
  let llCustomer = null;
  let llTierConfig = null;
  let llEarnRate = 4;
  let loyaltyInited = false;

  function waitForLoyaltyLion() {
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      if (window.loyaltylion && window.loyaltylion.customer) {
        clearInterval(poll);
        llCustomer = window.loyaltylion.customer;
        initLoyalty();
      } else if (window.loyaltylion && !window.loyaltylion.customer && attempts > 10) {
        clearInterval(poll); /* guest — keep loyalty hidden (avoid cart friction) */
      } else if (attempts > 50) {
        clearInterval(poll); /* no app embed (pre-Phase 6) — stay dormant */
      }
    }, 200);
  }

  function initLoyalty() {
    if (!llCustomer) return;
    loyaltyInited = true;

    let tierId = 0;
    try {
      const membership = llCustomer.loyaltyTierMembership;
      if (membership) tierId = membership.loyaltyTierId || 0;
    } catch (e) {
      /* noop */
    }
    const tierConfigs = config.loyaltyTiers || [];
    const tierIds = config.loyaltyTierIds || {};
    let tierPos = tierIds[String(tierId)];
    if (tierPos === undefined) tierPos = 0;
    llTierConfig = tierConfigs[tierPos] || tierConfigs[0] || { name: 'Member', bg: '#EFE9E1', color: '#1D1D1D', rate: 4 };
    llEarnRate = llTierConfig.rate || 4;

    renderLoyalty();
  }

  function renderLoyalty() {
    if (!loyaltyInited || !llCustomer) return;
    const wrap = q('[data-cc-drawer-loyalty]');
    if (!wrap) return;
    wrap.hidden = false;

    const badge = q('[data-cc-loyalty-badge]');
    if (badge) {
      badge.textContent = '★ ' + (llTierConfig.name || 'Member');
      badge.style.background = llTierConfig.bg;
      badge.style.color = llTierConfig.color;
    }
    const guest = q('[data-cc-loyalty-guest]');
    if (guest) guest.hidden = true;

    const detail = q('[data-cc-loyalty-detail]');
    const arrow = q('[data-cc-loyalty-arrow]');
    if (detail) detail.hidden = !loyaltyExpanded;
    if (arrow) arrow.classList.toggle('is-open', loyaltyExpanded);

    updateLoyaltyBalance();
    updateLoyaltyEarning();
    updateLoyaltyAction();
    updateLoyaltyBar();
  }

  function updateLoyaltyBalance() {
    const el = q('[data-cc-loyalty-balance]');
    if (!llCustomer || !el) return;
    const pts = llCustomer.pointsApproved || 0;
    el.innerHTML = pts.toLocaleString() + ' <span class="cc-drawer__loyalty-star">★</span>';
  }

  function updateLoyaltyBar() {
    const el = q('[data-cc-loyalty-bar-text]');
    if (!el || !llCustomer || isFlashing) return;
    const pts = llCustomer.pointsApproved || 0;
    const star = '<span class="cc-drawer__loyalty-star">★</span>';
    const existingCode = findUnusedCode();
    if (existingCode) {
      el.innerHTML = star + ' Balance: ' + pts.toLocaleString() + ' &middot; <strong>' + escapeHtml(existingCode.value) + ' code ready</strong>';
      return;
    }
    const best = findBestReward();
    if (best) {
      el.innerHTML = star + ' Balance: ' + pts.toLocaleString() + ' &middot; <strong>' + escapeHtml(best.value) + ' available</strong>';
      return;
    }
    el.innerHTML = star + ' Balance: ' + pts.toLocaleString();
  }

  function updateLoyaltyEarning() {
    const el = q('[data-cc-loyalty-earn]');
    if (!llCustomer || !el) return;
    const subtotal = nonRewardSubtotal();
    const earn = Math.floor((subtotal / 100) * llEarnRate);
    if (earn > 0) {
      const earnDollars = (earn * 0.01).toFixed(2);
      el.textContent =
        tr('Earn', 'Gana') + ' ' + earn.toLocaleString() + ' ★ ' + tr('worth', 'con valor de') + ' $' + earnDollars + ' ' + tr('with this order', 'con este pedido');
      el.style.display = '';
    } else {
      el.style.display = 'none';
    }
  }

  function updateLoyaltyAction() {
    const el = q('[data-cc-loyalty-action]');
    if (!el || !llCustomer) return;
    const existingCode = findUnusedCode();
    if (existingCode) {
      showCodeUI(el, existingCode.code, existingCode.value);
      return;
    }
    const best = findBestReward();
    if (best) {
      showRedeemUI(el, best);
      return;
    }
    const next = findNextReward();
    if (next) {
      const pts = llCustomer.pointsApproved || 0;
      const needed = next.stars - pts;
      el.innerHTML = '<p class="cc-drawer__loyalty-next">' + needed.toLocaleString() + ' more ★ until a ' + escapeHtml(next.value) + ' voucher</p>';
    } else {
      el.innerHTML = '';
    }
  }

  function findUnusedCode() {
    if (!llCustomer) return null;
    const claimed = llCustomer.claimedRewards || [];
    for (let i = claimed.length - 1; i >= 0; i--) {
      const cr = claimed[i];
      if (cr.state === 'approved' && cr.redeemable && cr.redeemable.code && cr.redeemable.usage_count === 0) {
        const value = cr.reward ? '$' + cr.reward.discount_amount : '';
        return { code: cr.redeemable.code, value: value };
      }
    }
    return null;
  }

  function findBestReward() {
    if (!llCustomer) return null;
    const redemptions = config.loyaltyRedemptions || [];
    const pts = llCustomer.pointsApproved || 0;
    const cartCents = nonRewardSubtotal();
    let best = null;
    for (const r of redemptions) {
      const dollarVal = parseInt(r.value.replace(/[^0-9]/g, ''), 10) || 0;
      if (pts >= r.stars && dollarVal * 100 <= cartCents) best = r;
    }
    return best;
  }

  function findNextReward() {
    if (!llCustomer) return null;
    const redemptions = config.loyaltyRedemptions || [];
    const pts = llCustomer.pointsApproved || 0;
    for (const r of redemptions) {
      if (pts < r.stars) return r;
    }
    return null;
  }

  let lastLoyaltyCodeShown = null; /* dedup: render hook repaints loyalty per section swap — track once per code per page (Empire fired per paint too; ours repaints 3x per open) */
  function showCodeUI(el, code, value) {
    if (lastLoyaltyCodeShown !== code) {
      lastLoyaltyCodeShown = code;
      trackEvent('cc_loyalty_code_shown', { value: value || '' });
    }
    const label = value ? 'Your ' + value + ' voucher is ready!' : 'Your voucher is ready!';
    el.innerHTML =
      '<div class="cc-drawer__loyalty-claimed">' +
      '<p class="cc-drawer__loyalty-claimed-label">' + escapeHtml(label) + '</p>' +
      '<div class="cc-drawer__loyalty-code-box">' +
      '<span class="cc-drawer__loyalty-code-text">' + escapeHtml(code) + '</span>' +
      '<button type="button" class="cc-drawer__loyalty-copy" data-cc-copy-code data-code="' + escapeHtml(code) + '" data-value="' + escapeHtml(value || '') + '">Copy</button>' +
      '</div>' +
      '<p class="cc-drawer__loyalty-claimed-note">Paste at checkout</p>' +
      '</div>';
  }

  function showRedeemUI(el, reward) {
    el.innerHTML =
      '<div class="cc-drawer__loyalty-offer">' +
      '<span class="cc-drawer__loyalty-offer-text">You have a <strong>' + escapeHtml(reward.value) + ' voucher</strong> available!</span>' +
      '<button type="button" class="cc-drawer__loyalty-redeem-btn" data-cc-redeem-now data-stars="' + reward.stars + '" data-value="' + escapeHtml(reward.value) + '" data-reward-id="' + reward.rewardId + '">Redeem</button>' +
      '</div>';
  }

  function claimReward(reward, btn) {
    const ll = window.loyaltylion;
    if (!ll || !ll.api || !ll.api.http) {
      btn.textContent = 'Redeem';
      btn.disabled = false;
      return;
    }
    const baseUrl = ll.api.http.baseUrl || 'https://sdk.loyaltylion.net';
    const headers = {};
    try {
      const h = ll.api.http.headers;
      if (h && h.forEach) h.forEach((v, k) => (headers[k] = v));
      else if (h) for (const k in h) headers[k] = h[k];
    } catch (e) {
      /* noop */
    }
    headers['Content-Type'] = 'application/json';

    fetch(baseUrl + '/customers/rewards/claim', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ reward_id: reward.rewardId }),
      credentials: 'omit',
    })
      .then((r) => r.json())
      .then((result) => {
        if (result && !result.error && result.claimed_reward && result.claimed_reward.redeemable) {
          const el = q('[data-cc-loyalty-action]');
          if (el) showCodeUI(el, result.claimed_reward.redeemable.code, reward.value);
          if (result.customer && result.customer.pointsApproved !== undefined) {
            llCustomer.pointsApproved = result.customer.pointsApproved;
            updateLoyaltyBalance();
          }
        } else {
          btn.textContent = 'Redeem';
          btn.disabled = false;
        }
      })
      .catch(() => {
        btn.textContent = 'Redeem';
        btn.disabled = false;
      });
  }

  /* rewards threshold flash (on the loyalty bar) */
  function checkThresholdFlash() {
    if (!tiers.length) return;
    const subtotal = nonRewardSubtotal();
    let unlocked = 0;
    let latestReward = '';
    for (let i = 0; i < tiers.length; i++) {
      if (subtotal >= tiers[i].amount) {
        unlocked = i + 1;
        latestReward = tiers[i].reward;
      }
    }
    if (lastUnlockedTiers >= 0 && unlocked > lastUnlockedTiers) {
      flashBar(latestReward + tr(' unlocked!', ' desbloqueado!'));
    }
    lastUnlockedTiers = unlocked;
  }

  function flashBar(message) {
    const bar = q('[data-cc-loyalty-bar]');
    const barText = q('[data-cc-loyalty-bar-text]');
    if (!bar) return;
    isFlashing = true;
    const origHTML = barText ? barText.innerHTML : '';
    bar.classList.add('is-flashing');
    if (barText) barText.innerHTML = '<strong>' + escapeHtml(message) + '</strong>';
    setTimeout(() => {
      isFlashing = false;
      const barNow = q('[data-cc-loyalty-bar]');
      const barTextNow = q('[data-cc-loyalty-bar-text]');
      if (barNow) barNow.classList.remove('is-flashing');
      if (barTextNow) barTextNow.innerHTML = origHTML;
      updateLoyaltyBar();
    }, 3000);
  }

  /* ---------- mobile ATC toast ---------- */
  let ccToast = null;
  let ccToastTimer = null;

  function createToastIfNeeded() {
    if (ccToast) return;
    ccToast = document.createElement('div');
    ccToast.className = 'cc-btm-toast';
    ccToast.innerHTML =
      '<span class="cc-btm-toast__text"></span><button type="button" class="cc-btm-toast__btn" data-cc-btm-view>' + tr('View Cart', 'Ver carrito') + '</button>';
    document.body.appendChild(ccToast);
    ccToast.querySelector('[data-cc-btm-view]').addEventListener('click', () => {
      hideBottomToast();
      const d = drawer();
      if (d && typeof d.open === 'function') d.open();
    });
  }

  function showBottomToast() {
    createToastIfNeeded();
    const el = subtotalEl();
    const total = el ? el.textContent.trim() : '';
    ccToast.querySelector('.cc-btm-toast__text').textContent =
      '✓ ' + tr('Added to cart', 'Agregado al carrito') + (total ? ' · ' + total : '');
    document.body.classList.add('cc-btm-toast-active');
    ccToast.classList.add('is-visible');
    clearTimeout(ccToastTimer);
    ccToastTimer = setTimeout(hideBottomToast, 3000);
  }

  function hideBottomToast() {
    if (!ccToast) return;
    ccToast.classList.remove('is-visible');
    document.body.classList.remove('cc-btm-toast-active');
  }

  /* ---------- utilities ---------- */
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
  }

  /* ---------- render hook ---------- */
  let renderQueued = false;

  function onRender(hub) {
    /* parse the fresh server state carried by the hub */
    const stateEl = hub.querySelector('[data-cc-cart-state]');
    if (stateEl) {
      try {
        state = JSON.parse(stateEl.textContent);
      } catch (e) {
        /* keep previous state */
      }
    }
    hubData = {
      realCount: parseInt(hub.dataset.realCount, 10) || 0,
      subtotalNr: parseInt(hub.dataset.subtotalNr, 10) || 0,
      insKey: hub.dataset.insKey || '',
    };

    if (renderQueued) return; /* coalesce double renders (publish + renderContents) */
    renderQueued = true;
    setTimeout(() => {
      renderQueued = false;
      runRenderTasks();
    }, 0);
  }

  function runRenderTasks() {
    const d = drawer();
    if (!d) return;
    toggleEmptyClasses(state.itemCount);

    /* subtotal target: optimistic (server total + pending edits) so a sync can't
       roll it backward while newer taps are in flight */
    if (!holdState) animateSubtotal(optimisticSubtotal());

    restoreNotesUI();

    const isOpen = d.classList.contains('active') || document.body.classList.contains('cc-drawer-open');
    if (isOpen) {
      initInsurance();
      manageRewardItems();
      updateInsurance();
      loadUpsells();
      renderLoyalty();
      checkThresholdFlash();
    }
    window.dispatchEvent(new CustomEvent('cc:cart-rendered', { detail: { state: state } }));
  }

  class CcCartDrawerHub extends HTMLElement {
    connectedCallback() {
      onRender(this);
    }
  }
  if (!customElements.get('cc-cart-drawer-hub')) {
    customElements.define('cc-cart-drawer-hub', CcCartDrawerHub);
  }

  /* ---------- Dawn CartDrawer patches (open/close/renderContents) ---------- */
  customElements.whenDefined('cart-drawer').then(() => {
    const CartDrawerClass = customElements.get('cart-drawer');
    const proto = CartDrawerClass.prototype;

    const origOpen = proto.open;
    proto.open = function (triggeredBy) {
      if (this._ccSuppressOpen) {
        /* mobile ATC — show the bottom toast instead of the drawer (Empire behavior) */
        this._ccSuppressOpen = false;
        showBottomToast();
        return;
      }
      const wasActive = this.classList.contains('active');
      origOpen.call(this, triggeredBy);
      if (!wasActive) {
        document.body.classList.add('cc-drawer-open');
        openedAt = Date.now();
        trackEvent('cc_drawer_open', { trigger: triggeredBy ? 'header_icon' : 'atc' });
        hideBottomToast();
        /* Empire re-read the cart on every open; keep the stale-tab safety but
           only REPAINT when the cart actually differs from what's rendered —
           the unconditional full repaint blinked the whole drawer on every
           open (Tony 2026-08-06). Fingerprint is order-independent. */
        if (triggeredBy) {
          fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
            .then((r) => r.json())
            .then((cart) => {
              const fp = (rows) => rows.map((x) => x.k + ':' + x.q).sort().join('|');
              const srv =
                fp((cart.items || []).map((i) => ({ k: i.key, q: i.quantity }))) + '#' + cart.total_price;
              const cur =
                fp(state.items.map((i) => ({ k: i.key, q: i.qty }))) + '#' + (state.totalPrice || 0);
              if (srv !== cur) return refreshFromServer('full').then(publishCartUpdate);
            })
            .catch(() => {});
        }
        /* run the open-gated tasks after Dawn's setTimeout adds .active */
        setTimeout(runRenderTasks, 30);
      }
    };

    const origClose = proto.close;
    proto.close = function () {
      const wasActive = this.classList.contains('active');
      origClose.call(this);
      if (wasActive) {
        document.body.classList.remove('cc-drawer-open');
        trackEvent('cc_drawer_closed', { time_open_ms: Date.now() - openedAt });
      }
    };

    const origRenderContents = proto.renderContents;
    proto.renderContents = function (parsedState) {
      /* below 720px an ATC render shows the toast, not the drawer (Empire) */
      if (window.innerWidth < 720 && !this.classList.contains('active')) {
        this._ccSuppressOpen = true;
      }
      origRenderContents.call(this, parsedState);
      if (this._ccSuppressOpen) {
        /* open() (suppressed) still fires the toast via the patched open above,
           but only after Dawn's setTimeout — make sure classes stay consistent */
        document.body.classList.remove('overflow-hidden');
      }
    };
  });

  /* ---------- event delegation (survives every section swap) ---------- */
  function delegatedRoot() {
    return document; /* cart-drawer children get replaced; document is stable */
  }

  delegatedRoot().addEventListener('pointerdown', (e) => {
    const qtyBtn = e.target.closest && e.target.closest('[data-cc-qty-change]');
    if (!qtyBtn || qtyBtn.disabled || !qtyBtn.closest('cart-drawer')) return;
    e.preventDefault();
    startHold(qtyBtn.getAttribute('data-cc-item-key'), parseInt(qtyBtn.getAttribute('data-cc-qty-change'), 10));
  });
  document.addEventListener('pointerup', stopHold);
  document.addEventListener('pointercancel', stopHold);

  delegatedRoot().addEventListener('click', (e) => {
    const t = e.target;
    if (!t.closest) return;

    const removeBtn = t.closest('[data-cc-item-remove]');
    if (removeBtn && removeBtn.closest('cart-drawer')) {
      const rowEl = removeBtn.closest('[data-cc-drawer-item]');
      const removeVariant = rowEl && rowEl.getAttribute('data-cc-remove-variant');
      if (removeVariant) {
        removeVariantLines(parseInt(removeVariant, 10)); /* rolls the subtotal itself */
      } else {
        setQtyTarget(removeBtn.getAttribute('data-cc-item-remove'), 0);
        animateSubtotal(optimisticSubtotal());
      }
      return;
    }
    const pickerBtn = t.closest('[data-cc-picker-select]');
    if (pickerBtn && pickerBtn.closest('cart-drawer')) {
      onPickerSelect(pickerBtn);
      return;
    }
    const upsellCard = t.closest('[data-cc-upsell-add]');
    if (upsellCard && upsellCard.closest('cart-drawer')) {
      onUpsellAdd(upsellCard);
      return;
    }
    const notesToggle = t.closest('[data-cc-notes-toggle]');
    if (notesToggle && notesToggle.closest('cart-drawer')) {
      onNotesToggle();
      return;
    }
    const loyaltyBar = t.closest('[data-cc-loyalty-bar]');
    if (loyaltyBar && loyaltyBar.closest('cart-drawer')) {
      loyaltyExpanded = !loyaltyExpanded;
      const detail = q('[data-cc-loyalty-detail]');
      const arrow = q('[data-cc-loyalty-arrow]');
      if (detail) detail.hidden = !loyaltyExpanded;
      if (arrow) arrow.classList.toggle('is-open', loyaltyExpanded);
      trackEvent('cc_loyalty_toggle', {
        expanded: loyaltyExpanded,
        tier: llTierConfig ? llTierConfig.name : llCustomer ? 'unknown' : 'guest',
      });
      return;
    }
    const copyBtn = t.closest('[data-cc-copy-code]');
    if (copyBtn && copyBtn.closest('cart-drawer')) {
      const code = copyBtn.dataset.code || '';
      const value = copyBtn.dataset.value || '';
      const done = () => {
        copyBtn.textContent = 'Copied!';
        trackEvent('cc_loyalty_code_copied', { value: value });
        setTimeout(() => {
          copyBtn.textContent = 'Copy';
        }, 2000);
      };
      if (navigator.clipboard) {
        navigator.clipboard.writeText(code).then(done);
      } else {
        const ta = document.createElement('textarea');
        ta.value = code;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      }
      return;
    }
    const redeemBtn = t.closest('[data-cc-redeem-now]');
    if (redeemBtn && redeemBtn.closest('cart-drawer')) {
      redeemBtn.textContent = 'Claiming...';
      redeemBtn.disabled = true;
      const reward = {
        stars: parseInt(redeemBtn.dataset.stars, 10) || 0,
        value: redeemBtn.dataset.value || '',
        rewardId: parseInt(redeemBtn.dataset.rewardId, 10) || 0,
      };
      trackEvent('cc_loyalty_redeem_click', { value: reward.value, stars: reward.stars });
      claimReward(reward, redeemBtn);
      return;
    }
    const checkoutBtn = t.closest('[data-cc-drawer-checkout]');
    if (checkoutBtn && checkoutBtn.closest('cart-drawer')) {
      const el = subtotalEl();
      trackEvent('cc_drawer_checkout_click', { subtotal: el ? el.textContent.trim() : '' });
      /* no return — let the submit proceed */
    }
  });

  delegatedRoot().addEventListener('change', (e) => {
    const check = e.target.closest && e.target.closest('[data-cc-protection-check]');
    if (check && check.closest('cart-drawer')) onInsToggle(check);
  });

  delegatedRoot().addEventListener('input', (e) => {
    const input = e.target.closest && e.target.closest('[data-cc-notes-input]');
    if (input && input.closest('cart-drawer')) onNotesInput(input);
  });

  /* ---------- boot ---------- */
  waitForLoyaltyLion();

  window.ccCartDrawer = {
    get state() {
      return state;
    },
    refresh: () => refreshFromServer('full'),
    /* server state + pending optimistic edits — the authoritative per-line
       quantities for anything painting over the items list (cc-volume-nudge) */
    effectiveItems: () => state.items.map((it) => Object.assign({}, it, { qty: effectiveQty(it) })),
    /* package protection for other surfaces (Build Your Once step 4, 2026-10-02):
       same opt-out key and add/remove lane as the drawer's own switch */
    insurance: {
      /* initInsurance() normally runs on drawer OPEN; surfaces that never open
         the drawer resolve the protection product lazily through this API */
      state: () => {
        initInsurance();
        return {
          enabled: insEnabled && insReady,
          defaultOn: insDefaultOn,
          inCart: !!hubData.insKey || state.items.some((it) => it.ins && it.qty > 0),
          price: insPriceCents,
          title: insTitle,
          sub: insSub,
          busy: isManagingInsurance,
          optedOut: insOptedOut(),
        };
      },
      set: (on) => {
        initInsurance();
        if (!insReady || isManagingInsurance) return false;
        if (on) {
          setInsOptOut(false);
          addInsurance();
        } else {
          setInsOptOut(true);
          removeInsurance();
        }
        return true;
      },
      /* the drawer's own default-on behaviour, once per page, for a closed drawer */
      autoAdd: () => {
        initInsurance();
        if (!insReady || !insDefaultOn || insOptedOut() || insAutoAddAttempted || isManagingInsurance || isManagingRewards) return false;
        if (hubData.insKey || state.items.some((it) => it.ins && it.qty > 0)) return false;
        insAutoAddAttempted = true;
        addInsurance();
        return true;
      },
    },
  };
})();
